import type { AssessedRow, ConcernType, ResolutionOption, ResolutionStrategy } from './types';

/**
 * Per-location fit checks for an "append this text" instruction.
 *
 * These are deterministic heuristics standing in for the model-backed
 * assessment pass. They exist to *triage attention* — to pull the handful of
 * odd locations out of a bulk edit so a human looks at them. They are not the
 * safety mechanism: every change still goes through the server-side dry run and
 * an explicit diff the user confirms. If assessment misses something, the user
 * still sees it in the preview before anything is written.
 */

const BULLET_LINE = /^\s*([-*\u2022\u00b7])\s+\S/;
const NUMBERED_LINE = /^\s*\d+[.)]\s+\S/;

/** Collapses whitespace so formatting differences do not hide a real match. */
const normalizeForCompare = (value: string): string =>
  value.toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * True when the description already contains the text, ignoring case and
 * whitespace layout. Appending in that case would duplicate the sentence.
 */
export const alreadyContains = (description: string, text: string): boolean => {
  const haystack = normalizeForCompare(description);
  const needle = normalizeForCompare(text);
  return needle.length > 0 && haystack.includes(needle);
};

/**
 * Returns the bullet character used by a list-formatted description, or null
 * when the text is ordinary prose.
 *
 * At least two bulleted lines are required: a single leading dash is far more
 * likely to be a hyphen or a dash-led sentence than a list.
 */
export const detectBulletMarker = (description: string): string | null => {
  const lines = description.split(/\r?\n/);
  let bulletCount = 0;
  let numberedCount = 0;
  let marker: string | null = null;

  for (const line of lines) {
    const bulletMatch = line.match(BULLET_LINE);
    if (bulletMatch) {
      bulletCount += 1;
      if (!marker) marker = bulletMatch[1];
      continue;
    }
    if (NUMBERED_LINE.test(line)) numberedCount += 1;
  }

  if (bulletCount >= 2) return marker;
  // Numbered lists get a neutral marker; continuing the numbering correctly is
  // more presumptuous than the user is likely to want from a bulk edit.
  if (numberedCount >= 2) return '-';
  return null;
};

/**
 * Produces the description that results from applying a strategy.
 *
 * `APPEND_SENTENCE` adds a terminating period to the *existing* copy only when
 * it lacks one, and otherwise leaves the supplier's wording byte-for-byte
 * intact.
 */
export const applyStrategy = (
  currentDescription: string,
  text: string,
  strategy: ResolutionStrategy,
  bulletMarker: string | null,
): string => {
  const current = currentDescription.trim();

  switch (strategy) {
    case 'SKIP':
      return currentDescription;
    case 'REPLACE_EMPTY':
      return text;
    case 'APPEND_BULLET': {
      const marker = bulletMarker ?? '-';
      return `${currentDescription.replace(/\s+$/, '')}\n${marker} ${text}`;
    }
    case 'APPEND_SENTENCE':
    default: {
      if (!current) return text;
      const separator = /[.!?]$/.test(current) ? ' ' : '. ';
      return `${current}${separator}${text}`;
    }
  }
};

/** The choices offered for a flagged location, safest option first. */
export const optionsForConcern = (concern: ConcernType): ResolutionOption[] => {
  switch (concern) {
    case 'ALREADY_PRESENT':
      return [
        { strategy: 'SKIP', label: 'Skip this one' },
        { strategy: 'APPEND_SENTENCE', label: 'Add it anyway' },
      ];
    case 'EMPTY_SOURCE':
      return [
        { strategy: 'SKIP', label: 'Leave it empty' },
        { strategy: 'REPLACE_EMPTY', label: 'Use it as the description' },
      ];
    case 'LIST_FORMAT':
    default:
      return [
        { strategy: 'APPEND_BULLET', label: 'Add as a new bullet' },
        { strategy: 'APPEND_SENTENCE', label: 'Add as a sentence' },
        { strategy: 'SKIP', label: 'Skip this one' },
      ];
  }
};

interface AssessInput {
  locationId: string;
  locationName: string;
  currentDescription: string;
  text: string;
}

/**
 * Classifies one location's description and pre-selects the safest sensible
 * strategy. A clean description comes back with `concern: null` and needs no
 * confirmation beyond the diff.
 */
export const assessRow = ({
  locationId,
  locationName,
  currentDescription,
  text,
}: AssessInput): AssessedRow => {
  const bulletMarker = detectBulletMarker(currentDescription);

  let concern: ConcernType | null = null;
  let rationale: string | null = null;
  let strategy: ResolutionStrategy = 'APPEND_SENTENCE';

  if (!currentDescription.trim()) {
    concern = 'EMPTY_SOURCE';
    rationale = 'has no description yet, so this text would become the whole description';
    strategy = 'SKIP';
  } else if (alreadyContains(currentDescription, text)) {
    concern = 'ALREADY_PRESENT';
    rationale = 'already mentions this, so adding it would repeat the same line twice';
    strategy = 'SKIP';
  } else if (bulletMarker) {
    concern = 'LIST_FORMAT';
    rationale = 'is written as a bulleted list, so a trailing sentence would break the formatting';
    strategy = 'APPEND_BULLET';
  }

  return {
    locationId,
    locationName,
    currentDescription,
    concern,
    rationale,
    strategy,
    proposedDescription: applyStrategy(currentDescription, text, strategy, bulletMarker),
  };
};
